/**
 * One system (docs/systems.md "Systems view"): who it is (the registry: name, logo, kind, description, owner, vendor,
 * link; owners get "Edit" into Settings → Systems), what comes in and goes out of it and within it with their state,
 * the map around it, its data models (each opens the data search for it, as Now does), the connections it is reached
 * over, its newest failed documents (open in the peek) and "Search this system's data". The range is in the URL, a
 * history step, the selected arrow too; peeks follow the peek rules; a system or an integration opens its page.
 */
import { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import dayjs from 'dayjs';
import { Api } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { useEnv } from '../lib/envs.tsx';
import { useSearchParams } from '../lib/liveparams.ts';
import { rememberSearch } from '../lib/hooks.ts';
import { count } from '../lib/format.ts';
import { age } from '../lib/freshness.ts';
import { integrationsOf } from '../lib/integrations.ts';
import { integrationPath, integrationQuery, rangeFrom, rangeWords, readRange, systemPath, systemQuery, withRange } from '../lib/systemsview.ts';
import { resolveSystem, stripSystem, systemColor, systemIcon, systemInfo, systemLogo, systemName } from '../lib/systems.ts';
import { KIND_LABEL } from '../lib/systemedit.ts';
import { Shell } from '../shell/Shell.tsx';
import { NounIcon, Surface, cx, noun } from '../design/primitives.tsx';
import { SystemMark } from '../components/SystemEditor.tsx';
import { SystemMap } from '../components/SystemMap.tsx';
import { BackIcon, PencilIcon, SearchIcon } from '../components/icons.tsx';
import { Peek, PeekLayout, usePeek } from '../peek/Peek.tsx';
import { useClock } from '../now/useLive.ts';
import { useRangeLive } from '../systems/useRangeLive.ts';
import { ActionLink, IntegrationRow, RangePills, Section, NameToggle } from '../systems/parts.tsx';

const POLL_MS = 60_000;
const search = (q: string) => `/s?${new URLSearchParams({ q })}`;

export function SystemPage() {
  const { code: raw = '' } = useParams();
  const code = resolveSystem(raw.trim().toUpperCase());
  const { tenant, canManage } = useAuth();
  const tid = tenant?.id ?? '';
  const navigate = useNavigate();
  const { link, current } = useEnv();
  const [params, setParams] = useSearchParams();
  const range = readRange(params);
  const arc = params.get('arc');
  const peek = usePeek();
  const now = useClock(30_000);
  const time = useMemo(() => new URLSearchParams({ from: rangeFrom(range) }), [range]);

  const live = useRangeLive((r) => Api.systemOverview(tid, code, new URLSearchParams({ from: rangeFrom(r) })), range, POLL_MS, [tid, code, current?.id]);
  const o = live.data;
  const shownRange = o?.range ?? range;
  const info = systemInfo(code);
  const split = useMemo(() => (o ? integrationsOf(o.integrations, o.code) : null), [o]);
  const failing = o?.integrations.filter((i) => i.status === 'failing').length ?? 0;
  const overdue = o?.integrations.filter((i) => i.status === 'quiet').length ?? 0;
  const documents = o?.dataModels.reduce((n, d) => n + d.documents, 0) ?? 0;
  const query = systemQuery(code, info?.aliases ?? [], o?.dataModels.map((d) => d.cls) ?? info?.classes ?? []);
  const go = (q: string) => {
    if (!q) return;
    rememberSearch(q);
    void navigate(link(search(q)));
  };
  const unknown = !!o && !info && o.integrations.length === 0 && o.dataModels.length === 0;

  return (
    <Shell search={{ value: '', onSubmit: go, size: 'compact' }}>
      <PeekLayout open={peek.stack.length > 0} peek={<Peek time={time} />}>
        <div className="sys-page" data-system-page={code}>
          <Link to={link('/systems')} className="sys-crumb"><BackIcon size={14} />Systems</Link>

          <header className="sys-head mt-4">
            <div className="flex items-center gap-4 min-w-0">
              <SystemMark code={code} color={systemColor(code)} icon={systemIcon(code)} logo={systemLogo(code)} size={64} className="rounded-[16px]" />
              <div className="min-w-0 flex flex-col gap-1">
                <h1 className="m-0 text-[34px] leading-[40px] font-semibold tracking-tight truncate">{systemName(code)}</h1>
                <p className="m-0 text-small text-n8 flex items-center gap-x-2 gap-y-1 flex-wrap">
                  <span className="font-mono text-n9">{systemName(code) === code ? 'System' : code}</span>
                  {info?.aliases.length ? <span className="text-n7">also {info.aliases.join(', ')}</span> : null}
                  {info?.kind && <><Dot /><span>{KIND_LABEL[info.kind] ?? info.kind}</span></>}
                  {info?.vendor && <><Dot /><span>{info.vendor}</span></>}
                  {info?.owner && <><Dot /><span>Owner {info.owner}</span></>}
                  {info?.url && /^https?:\/\//i.test(info.url) && <><Dot /><a href={info.url} target="_blank" rel="noopener noreferrer" className="text-accent no-underline hover:underline">{info.url.replace(/^https?:\/\//i, '').replace(/\/$/, '')}</a></>}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <NameToggle />
              <RangePills value={range} onChange={(r) => setParams((p) => withRange(p, r))} />
              {canManage && info && (
                <Link to={link(`/settings/systems?${new URLSearchParams({ system: code })}`)} title="Edit the system in Settings → Systems"
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-transparent text-small font-medium text-n8 no-underline hover:bg-n3 hover:text-n10">
                  <PencilIcon size={14} />Edit
                </Link>
              )}
              <ActionLink to={link(search(query))}><SearchIcon size={15} />Search this system’s data</ActionLink>
            </div>
          </header>
          {info?.description && <p className="m-0 mt-5 max-w-[78ch] text-body text-n9 leading-relaxed whitespace-pre-line">{info.description}</p>}

          {live.error && !o && <p className="m-0 mt-8 text-body text-fail">{live.error}</p>}
          {!o && !live.error && <div className="mt-8 flex flex-col gap-4" aria-busy="true"><span className="mv-shimmer rounded-chip h-6 w-80" /><span className="mv-shimmer rounded-card h-56" /></div>}
          {unknown && <p className="m-0 mt-8 text-body text-n8">No system {code} is known: nothing in the registry, no integrations or data models with this code.</p>}

          {o && !unknown && (
            <>
              <p className="m-0 mt-6 text-body text-n9 flex items-center gap-x-4 gap-y-1 flex-wrap" data-system-sentence="">
                <span>{count(o.integrations.length)} {o.integrations.length === 1 ? 'integration' : 'integrations'}</span>
                {failing > 0 && <span className="inline-flex items-center gap-2"><i className="size-2 rounded-full bg-fail" aria-hidden="true" />{count(failing)} failing</span>}
                {overdue > 0 && <span className="inline-flex items-center gap-2"><i className="size-2 rounded-full bg-warn" aria-hidden="true" />{count(overdue)} overdue</span>}
                <span className="text-n8">{count(documents)} documents in the {rangeWords(shownRange)}</span>
              </p>

              {split && o.integrations.length > 0 && (
                <Section title="Integrations" fact={rangeWords(shownRange)} className="mt-8" id="sys-integrations">
                  <div className={cx('sys-flows', split.within.length === 0 && 'two')}>
                    {([['in', 'Coming in', split.in, 'Nothing comes in'], ['out', 'Going out', split.out, 'Nothing goes out'], ['within', 'Within', split.within, 'Nothing within the system']] as const).filter(([dir, , list]) => dir !== 'within' || list.length > 0).map(([dir, title, list, none]) => (
                      <Surface key={dir} className="p-1.5 flex flex-col min-w-0" data-direction={dir}>
                        <div className="flex items-baseline justify-between px-2.5 pt-2 pb-1">
                          <span className="text-small font-semibold text-n10">{title}</span>
                          <span className="text-micro text-n7 tabular-nums">{count(list.length)}</span>
                        </div>
                        {list.length ? (
                          <div className="sys-rows sys-narrow">
                            {list.map((i) => <IntegrationRow key={`${i.process}|${i.from}|${i.to}`} i={i} now={now} other={{ dir }} />)}
                          </div>
                        ) : <p className="m-0 px-2.5 pb-3 pt-1 text-small text-n7">{none}</p>}
                      </Surface>
                    ))}
                  </div>
                </Section>
              )}

              {o.integrations.some((i) => i.from && i.to && i.from !== i.to) && (
                <Section title={`Around ${systemName(code)}`} fact={rangeWords(shownRange)} className="mt-10" id="sys-around">
                  <SystemMap integrations={o.integrations} selected={arc} windowLabel={rangeWords(shownRange)} openWhat="its page" bleed
                    onSelect={(a) => setParams((p) => { const n = new URLSearchParams(p); if (a) n.set('arc', a); else n.delete('arc'); return n; })}
                    onOpenSystem={(c) => { if (c !== code) void navigate(link(systemPath(c))); }}
                    onOpenProcess={(process) => void navigate(link(integrationPath(process)))}
                    onSearch={(process) => go(integrationQuery(process))} />
                </Section>
              )}

              <div className="sys-body two mt-10">
                <div className="flex flex-col gap-10 min-w-0">
                  <Section title="Data models" fact={count(o.dataModels.length)} id="sys-models">
                    {o.dataModels.length ? (
                      <Surface className="p-1.5">
                        <div className="sys-rows">
                          {o.dataModels.map((d) => (
                            <Link key={d.cls} to={link(search(`@class=${d.cls}`))} data-model={d.cls}
                              className="grid grid-cols-[18px_minmax(0,1fr)_auto_auto] gap-3 items-center px-3 py-2.5 rounded-chip no-underline text-small text-n10 hover:bg-n3">
                              <NounIcon kind="document" size={16} />
                              <span className="flex flex-col min-w-0"><span className="font-medium truncate">{cap(noun(d.cls, 2))}</span><span className="font-mono text-micro text-n7 truncate">{d.cls}</span></span>
                              <span className="tabular-nums text-n9 whitespace-nowrap">{count(d.documents)}<span className="text-n7"> docs</span></span>
                              <span className={cx('tabular-nums whitespace-nowrap w-20 text-right', d.failed ? 'text-fail' : 'text-n7')}>{d.failed ? `${count(d.failed)} failed` : '—'}</span>
                            </Link>
                          ))}
                        </div>
                      </Surface>
                    ) : <p className="m-0 text-small text-n7">No data models found in this system yet.</p>}
                  </Section>

                  {o.connections.length > 0 && (
                    <Section title="Connections" fact={count(o.connections.length)} id="sys-connections">
                      <Surface className="p-1.5">
                        <div className="sys-rows">
                          {o.connections.map((c) => {
                            const warn = o.warnings.find((w) => w.connection === c.name);
                            return (
                              <div key={c.name} className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-3 items-center px-3 py-2.5 text-small">
                                <span className="flex flex-col min-w-0">
                                  <span className="truncate text-n10" title={c.name}>{stripSystem(c.name) || c.name}</span>
                                  {warn && <span className="text-micro text-warn">named for {systemName(warn.namedFor)}, the data says {systemName(warn.dataSays)}</span>}
                                </span>
                                <span className="tabular-nums text-n9 whitespace-nowrap">{count(c.count)}<span className="text-n7"> docs</span></span>
                                <span className="text-n7 whitespace-nowrap w-24 text-right">{c.last ? `${age(c.last, now)} ago` : '—'}</span>
                              </div>
                            );
                          })}
                        </div>
                      </Surface>
                      <p className="m-0 text-micro text-n7">The technical routes into the system (sender meta <span className="font-mono">connection</span>), all time.</p>
                    </Section>
                  )}
                </div>

                <Section title="Latest failures" fact="last 7 days" id="sys-failures">
                  {o.failures.length ? (
                    <Surface className="p-1.5">
                      <div className="sys-rows">
                        {o.failures.map((f) => (
                          <div key={`${f.vaultId}:${f.uuid}`} role="button" tabIndex={0} data-failure={f.uuid}
                            onClick={() => peek.open({ kind: 'document', ref: { vaultId: f.vaultId, uuid: f.uuid, day: f.day } })}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); peek.open({ kind: 'document', ref: { vaultId: f.vaultId, uuid: f.uuid, day: f.day } }); } }}
                            className="grid grid-cols-[56px_minmax(0,1fr)] gap-3 px-3 py-2.5 rounded-chip cursor-pointer text-small hover:bg-n3 focus-visible:outline-2 focus-visible:outline-accent">
                            <span className="font-mono text-micro text-n7 pt-0.5 whitespace-nowrap">{dayjs(f.at).isSame(dayjs(now), 'day') ? dayjs(f.at).format('HH:mm') : dayjs(f.at).format('D.M.')}</span>
                            <span className="flex flex-col gap-1 min-w-0">
                              <span className="text-n10 line-clamp-2">{f.info ?? `${cap(noun(f.cls))} failed`}</span>
                              <span className="flex items-center gap-2 text-micro text-n7 min-w-0">
                                <i className="size-1.5 rounded-full bg-fail shrink-0" aria-hidden="true" />
                                <span className="truncate">{noun(f.cls)}{f.process && <> · {stripSystem(f.process)}</>}</span>
                                {f.executionId && (
                                  <button type="button" className="ml-auto shrink-0 bg-transparent border-0 p-0 cursor-pointer text-micro text-n8 hover:text-n10 hover:underline"
                                    onClick={(e) => { e.stopPropagation(); peek.open({ kind: 'run', executionId: f.executionId! }); }}>run</button>
                                )}
                              </span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </Surface>
                  ) : <p className="m-0 text-small text-n7">No failed documents in the last 7 days.</p>}
                </Section>
              </div>
              {o.partial && <p className="m-0 mt-6 text-micro text-warn">Some vaults did not answer; what is shown may be incomplete.</p>}
            </>
          )}
        </div>
      </PeekLayout>
    </Shell>
  );
}

const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
function Dot() {
  return <span className="text-n6" aria-hidden="true">·</span>;
}
